import React, { useState, useEffect } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonButton,
  IonBackButton, IonList, IonItem, IonLabel, IonInput, IonTextarea, IonSelect,
  IonSelectOption, IonDatetime, IonModal, IonIcon, IonChip, IonCard,
  IonCardHeader, IonCardTitle, IonCardContent, IonItemDivider, IonToggle,
  IonAlert, IonBadge,
} from '@ionic/react';
import { 
  personCircle, school, medical, call, alert, heart, add, close, trash,
  calendar, colorPalette, information,
} from 'ionicons/icons';
import { format, differenceInYears } from 'date-fns';
import { supabase } from '../services/supabase';
import type { FamilyMember } from '../types';
import './FamilyMemberProfile.css';

interface FamilyMemberProfileProps {
  memberId?: string;
  householdId: string;
  onSave?: (member: FamilyMember) => void;
  onClose?: () => void;
}

interface MemberProfile {
  school?: {
    name?: string;
    grade?: string;
    teacher?: string;
    phone?: string;
    address?: string;
    notes?: string;
  };
  medical?: {
    allergies?: string[];
    medications?: string[];
    conditions?: string[];
    blood_type?: string;
    doctor?: string;
    doctor_phone?: string;
    insurance?: string;
    notes?: string;
  };
  emergency_contacts?: Array<{
    name: string;
    relationship: string;
    phone: string;
    priority: number;
  }>;
  preferences?: {
    nickname?: string;
    favorite_color?: string;
    interests?: string[];
    dietary_restrictions?: string[];
  };
  notes?: string;
}

const EMOJI_OPTIONS = ['👤', '👨', '👩', '👦', '👧', '👶', '🧒', '👴', '👵', '🧑', '👱', '🧔', '🐶', '🐱', '🐰'];
const COLOR_OPTIONS = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f43f5e', '#84cc16', '#06b6d4'];
const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const GRADE_OPTIONS = ['Pre-K', 'Kindergarten', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th', 'College', 'N/A'];

const FamilyMemberProfile: React.FC<FamilyMemberProfileProps> = ({
  memberId,
  householdId,
  onSave,
  onClose,
}) => {
  const [member, setMember] = useState<Partial<FamilyMember>>({
    name: '',
    avatar_emoji: '👤',
    color: '#6366f1',
    member_type: 'adult',
    is_parent: false,
  });
  const [profile, setProfile] = useState<MemberProfile>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [newAllergy, setNewAllergy] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', relationship: '', phone: '', priority: 1 });

  // Load existing member
  useEffect(() => {
    if (memberId) {
      const loadMember = async () => {
        const { data } = await supabase
          .from('family_members')
          .select('*')
          .eq('id', memberId)
          .single();
        
        if (data) {
          setMember(data);
          setProfile(data.profile || {});
        }
      };
      loadMember();
    }
  }, [memberId]);

  const age = member.birth_date 
    ? differenceInYears(new Date(), new Date(member.birth_date))
    : null;

  const handleSave = async () => {
    const memberData = {
      ...member,
      household_id: householdId,
      profile,
    };

    let result;
    if (memberId) {
      result = await supabase
        .from('family_members')
        .update(memberData)
        .eq('id', memberId)
        .select()
        .single();
    } else {
      result = await supabase
        .from('family_members')
        .insert(memberData)
        .select()
        .single();
    }

    if (result.data) {
      onSave?.(result.data);
    }
  };

  const addAllergy = () => {
    if (!newAllergy.trim()) return;
    setProfile(prev => ({
      ...prev,
      medical: {
        ...prev.medical,
        allergies: [...(prev.medical?.allergies || []), newAllergy.trim()],
      },
    }));
    setNewAllergy('');
  };

  const removeAllergy = (index: number) => {
    setProfile(prev => ({
      ...prev,
      medical: {
        ...prev.medical,
        allergies: prev.medical?.allergies?.filter((_, i) => i !== index),
      },
    }));
  };

  const addInterest = () => {
    if (!newInterest.trim()) return;
    setProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        interests: [...(prev.preferences?.interests || []), newInterest.trim()],
      },
    }));
    setNewInterest('');
  };

  const addEmergencyContact = () => {
    if (!newContact.name || !newContact.phone) return;
    setProfile(prev => ({
      ...prev,
      emergency_contacts: [
        ...(prev.emergency_contacts || []),
        { ...newContact, priority: (prev.emergency_contacts?.length || 0) + 1 },
      ],
    }));
    setNewContact({ name: '', relationship: '', phone: '', priority: 1 });
    setShowEmergencyModal(false);
  };

  const removeEmergencyContact = (index: number) => {
    setProfile(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts?.filter((_, i) => i !== index),
    }));
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>{memberId ? 'Edit' : 'Add'} Family Member</IonTitle>
          <IonButtons slot="end">
            <IonButton strong onClick={handleSave}>Save</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="family-member-profile">
        {/* Basic Info */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={personCircle} /> Basic Information
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {/* Avatar Preview */}
            <div className="avatar-preview" style={{ backgroundColor: member.color }}>
              {member.avatar_emoji}
            </div>

            <IonItem>
              <IonLabel position="stacked">Name *</IonLabel>
              <IonInput
                value={member.name}
                onIonChange={e => setMember(prev => ({ ...prev, name: e.detail.value || '' }))}
                placeholder="Enter name"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Nickname</IonLabel>
              <IonInput
                value={profile.preferences?.nickname}
                onIonChange={e => setProfile(prev => ({
                  ...prev,
                  preferences: { ...prev.preferences, nickname: e.detail.value || '' },
                }))}
                placeholder="What do they go by?"
              />
            </IonItem>

            <IonItem>
              <IonLabel>Type</IonLabel>
              <IonSelect
                value={member.member_type}
                onIonChange={e => setMember(prev => ({ ...prev, member_type: e.detail.value }))}
              >
                <IonSelectOption value="adult">Adult</IonSelectOption>
                <IonSelectOption value="teen">Teen (13-17)</IonSelectOption>
                <IonSelectOption value="child">Child (0-12)</IonSelectOption>
                <IonSelectOption value="pet">Pet</IonSelectOption>
              </IonSelect>
            </IonItem>

            <IonItem button onClick={() => setShowDatePicker(true)}>
              <IonLabel>Birth Date</IonLabel>
              <span slot="end">
                {member.birth_date 
                  ? `${format(new Date(member.birth_date), 'MMM d, yyyy')} (${age} yrs)`
                  : 'Not set'}
              </span>
            </IonItem>

            {member.member_type === 'adult' && (
              <IonItem>
                <IonLabel>Is Parent/Admin?</IonLabel>
                <IonToggle
                  checked={member.is_parent}
                  onIonChange={e => setMember(prev => ({ ...prev, is_parent: e.detail.checked }))}
                />
              </IonItem>
            )}

            <IonItem>
              <IonLabel position="stacked">Avatar</IonLabel>
              <div className="emoji-picker">
                {EMOJI_OPTIONS.map(emoji => (
                  <button
                    key={emoji}
                    className={`emoji-option ${member.avatar_emoji === emoji ? 'selected' : ''}`}
                    onClick={() => setMember(prev => ({ ...prev, avatar_emoji: emoji }))}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Color</IonLabel>
              <div className="color-picker">
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color}
                    className={`color-option ${member.color === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setMember(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Phone</IonLabel>
              <IonInput
                type="tel"
                value={member.phone}
                onIonChange={e => setMember(prev => ({ ...prev, phone: e.detail.value || '' }))}
                placeholder="555-123-4567"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Email</IonLabel>
              <IonInput
                type="email"
                value={member.email}
                onIonChange={e => setMember(prev => ({ ...prev, email: e.detail.value || '' }))}
                placeholder="email@example.com"
              />
            </IonItem>
          </IonCardContent>
        </IonCard>

        {/* School Info (for kids) */}
        {(member.member_type === 'child' || member.member_type === 'teen') && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={school} /> School Information
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonLabel position="stacked">School Name</IonLabel>
                <IonInput
                  value={profile.school?.name}
                  onIonChange={e => setProfile(prev => ({
                    ...prev,
                    school: { ...prev.school, name: e.detail.value || '' },
                  }))}
                  placeholder="Lincoln Elementary"
                />
              </IonItem>

              <IonItem>
                <IonLabel>Grade</IonLabel>
                <IonSelect
                  value={profile.school?.grade}
                  onIonChange={e => setProfile(prev => ({
                    ...prev,
                    school: { ...prev.school, grade: e.detail.value },
                  }))}
                >
                  {GRADE_OPTIONS.map(grade => (
                    <IonSelectOption key={grade} value={grade}>{grade}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Teacher</IonLabel>
                <IonInput
                  value={profile.school?.teacher}
                  onIonChange={e => setProfile(prev => ({
                    ...prev,
                    school: { ...prev.school, teacher: e.detail.value || '' },
                  }))}
                  placeholder="Mrs. Smith"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">School Phone</IonLabel>
                <IonInput
                  type="tel"
                  value={profile.school?.phone}
                  onIonChange={e => setProfile(prev => ({
                    ...prev,
                    school: { ...prev.school, phone: e.detail.value || '' },
                  }))}
                  placeholder="555-123-4567"
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Notes</IonLabel>
                <IonTextarea
                  value={profile.school?.notes}
                  onIonChange={e => setProfile(prev => ({
                    ...prev,
                    school: { ...prev.school, notes: e.detail.value || '' },
                  }))}
                  placeholder="Pickup at door 3, aftercare until 5pm..."
                  rows={3}
                />
              </IonItem>
            </IonCardContent>
          </IonCard>
        )}

        {/* Medical Info */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={medical} /> Medical Information
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {/* Allergies */}
            <IonItemDivider>
              <IonIcon icon={alert} slot="start" />
              Allergies
            </IonItemDivider>
            <div className="chip-container">
              {profile.medical?.allergies?.map((allergy, index) => (
                <IonChip key={index} color="danger" onClick={() => removeAllergy(index)}>
                  {allergy}
                  <IonIcon icon={close} />
                </IonChip>
              ))}
              {(!profile.medical?.allergies || profile.medical.allergies.length === 0) && (
                <span className="no-items">No allergies listed</span>
              )}
            </div>
            <IonItem>
              <IonInput
                value={newAllergy}
                onIonChange={e => setNewAllergy(e.detail.value || '')}
                placeholder="Add allergy (e.g., peanuts)"
                onKeyDown={e => e.key === 'Enter' && addAllergy()}
              />
              <IonButton slot="end" onClick={addAllergy}>
                <IonIcon icon={add} />
              </IonButton>
            </IonItem>

            <IonItem>
              <IonLabel>Blood Type</IonLabel>
              <IonSelect
                value={profile.medical?.blood_type}
                onIonChange={e => setProfile(prev => ({
                  ...prev,
                  medical: { ...prev.medical, blood_type: e.detail.value },
                }))}
              >
                {BLOOD_TYPES.map(type => (
                  <IonSelectOption key={type} value={type}>{type}</IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Doctor</IonLabel>
              <IonInput
                value={profile.medical?.doctor}
                onIonChange={e => setProfile(prev => ({
                  ...prev,
                  medical: { ...prev.medical, doctor: e.detail.value || '' },
                }))}
                placeholder="Dr. Johnson"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Doctor Phone</IonLabel>
              <IonInput
                type="tel"
                value={profile.medical?.doctor_phone}
                onIonChange={e => setProfile(prev => ({
                  ...prev,
                  medical: { ...prev.medical, doctor_phone: e.detail.value || '' },
                }))}
                placeholder="555-123-4567"
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Medical Notes</IonLabel>
              <IonTextarea
                value={profile.medical?.notes}
                onIonChange={e => setProfile(prev => ({
                  ...prev,
                  medical: { ...prev.medical, notes: e.detail.value || '' },
                }))}
                placeholder="Medications, conditions, special needs..."
                rows={3}
              />
            </IonItem>
          </IonCardContent>
        </IonCard>

        {/* Emergency Contacts */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={call} /> Emergency Contacts
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              {profile.emergency_contacts?.map((contact, index) => (
                <IonItem key={index}>
                  <IonLabel>
                    <h2>{contact.name}</h2>
                    <p>{contact.relationship} • {contact.phone}</p>
                  </IonLabel>
                  <IonBadge slot="end">#{contact.priority}</IonBadge>
                  <IonButton fill="clear" color="danger" onClick={() => removeEmergencyContact(index)}>
                    <IonIcon icon={trash} />
                  </IonButton>
                </IonItem>
              ))}
              {(!profile.emergency_contacts || profile.emergency_contacts.length === 0) && (
                <IonItem>
                  <IonLabel className="ion-text-center">
                    <p>No emergency contacts added</p>
                  </IonLabel>
                </IonItem>
              )}
            </IonList>
            <IonButton expand="block" onClick={() => setShowEmergencyModal(true)}>
              <IonIcon icon={add} slot="start" />
              Add Emergency Contact
            </IonButton>
          </IonCardContent>
        </IonCard>

        {/* Interests (for kids) */}
        {(member.member_type === 'child' || member.member_type === 'teen') && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={heart} /> Interests & Preferences
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="chip-container">
                {profile.preferences?.interests?.map((interest, index) => (
                  <IonChip key={index} color="primary">
                    {interest}
                    <IonIcon icon={close} onClick={() => {
                      setProfile(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          interests: prev.preferences?.interests?.filter((_, i) => i !== index),
                        },
                      }));
                    }} />
                  </IonChip>
                ))}
              </div>
              <IonItem>
                <IonInput
                  value={newInterest}
                  onIonChange={e => setNewInterest(e.detail.value || '')}
                  placeholder="Add interest (e.g., soccer, minecraft)"
                  onKeyDown={e => e.key === 'Enter' && addInterest()}
                />
                <IonButton slot="end" onClick={addInterest}>
                  <IonIcon icon={add} />
                </IonButton>
              </IonItem>
            </IonCardContent>
          </IonCard>
        )}

        {/* General Notes */}
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={information} /> Notes
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonTextarea
              value={profile.notes}
              onIonChange={e => setProfile(prev => ({ ...prev, notes: e.detail.value || '' }))}
              placeholder="Any other important information..."
              rows={4}
            />
          </IonCardContent>
        </IonCard>

        {/* Date Picker Modal */}
        <IonModal isOpen={showDatePicker} onDidDismiss={() => setShowDatePicker(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Birth Date</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowDatePicker(false)}>Done</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonDatetime
              presentation="date"
              value={member.birth_date}
              onIonChange={e => {
                const value = e.detail.value;
                if (typeof value === 'string') {
                  setMember(prev => ({ ...prev, birth_date: value.split('T')[0] }));
                }
              }}
              max={new Date().toISOString()}
            />
          </IonContent>
        </IonModal>

        {/* Emergency Contact Modal */}
        <IonModal isOpen={showEmergencyModal} onDidDismiss={() => setShowEmergencyModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Add Emergency Contact</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowEmergencyModal(false)}>Cancel</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Name *</IonLabel>
                <IonInput
                  value={newContact.name}
                  onIonChange={e => setNewContact(prev => ({ ...prev, name: e.detail.value || '' }))}
                  placeholder="Grandma Sue"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Relationship</IonLabel>
                <IonInput
                  value={newContact.relationship}
                  onIonChange={e => setNewContact(prev => ({ ...prev, relationship: e.detail.value || '' }))}
                  placeholder="Grandmother"
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Phone *</IonLabel>
                <IonInput
                  type="tel"
                  value={newContact.phone}
                  onIonChange={e => setNewContact(prev => ({ ...prev, phone: e.detail.value || '' }))}
                  placeholder="555-123-4567"
                />
              </IonItem>
            </IonList>
            <IonButton expand="block" onClick={addEmergencyContact}>
              Add Contact
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default FamilyMemberProfile;
